import React, { Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { shouldRefetchOnRefocus } from '../hooks/refocusThrottle';
import { subscribePlanChanged } from '../lib/planSync';
import { BARRA_ALTO_CLASS } from '../lib/medidasDelChasis';
import {
  useCreditosDeLaSesion, cargarCreditosSiFaltan, refrescarCreditos, asegurarDueno, tomarCopiaDelPanel,
} from '../lib/creditosDeLaSesion';
import ChipDeCreditos from './ChipDeCreditos';
import OfflinePill from './OfflinePill';
import UserMenu from './UserMenu';

/* dashboard-rediseno (4.2 del encargo y seccion "Barra superior" del documento): LA BARRA DE ARRIBA DEL CHASIS.

   De izquierda a derecha: logo; "Offline mode" (solo la app sin red); el chip de creditos con su panel; "Buy
   credits" (para quien lo ve hoy); el chip del plan; el nombre y el avatar, que abre el MISMO menu del usuario de
   hoy. Se pinta en TODAS las pantallas del chasis.

   QUIEN VE QUE, CON LAS REGLAS DE HOY:
   - "Buy credits": la condicion del boton del Panel (`!user.is_guest` y tener documentos), leida de lo que la barra
     sabe (`documents_used` de `/auth/plan-info`).
   - El chip del plan: "Guest" si `is_guest`, "Pro" si el plan es pro, "Free" si no (la insignia del Panel).
   - El nombre: solo con cuenta, nunca el invitado.

   LOS CREDITOS, SIN LLAMADAS NUEVAS AL NAVEGAR (ver `lib/creditosDeLaSesion.js`). En el Panel los publica el Panel;
   fuera de el, la barra hace la primera carga una vez y refresca en los momentos de hoy: el cambio de plan y la
   vuelta a la pestana con el freno de hoy. En el Panel la barra no pide nada: el Panel ya lo hace. */

// La ventana de compra importa `lib/api`: se carga en diferido para que montar el chasis no arrastre `src/i18n.js`.
const CreditPackModal = React.lazy(() => import('./CreditPackModal'));

const RUTA_DEL_PANEL = '/dashboard';

/* borrar-rotas (A.6 a, pedido de Carlos del 28-oct): el borde izquierdo del nombre, en la misma linea que el borde
   izquierdo de los iconos del menu lateral. La barra deja 16 de aire; los iconos del menu empiezan a 28 en la
   computadora (el menu de 248: 12 de la lista y 16 del renglon) y a 31.5 en la tablet (el menu de iconos de 80, con
   el icono centrado). Medido en Chrome: diferencia 0 en computadora y tablet (bench_eq/borrar_rotas). Con el menu de
   la tablet abierto como menu completo sus iconos pasan a 28 y quedan 3.5 a la izquierda del nombre; se alinea con el
   menu de iconos, que es el que esta a la vista casi siempre. */
export const ALINEACION_DEL_NOMBRE = 'md:ml-[15.5px] lg:ml-3';

function useRutaDeLaBarra() {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useLocation().pathname || '';
  } catch {
    return '';
  }
}

/* dashboard-rediseno, vuelta 2 (D4): LA BARRA NUNCA SE SALE, de 320 a 2560 y en ningun estado. Lo que se ve depende
   de lo que CABE, no de un ancho fijo (en la app sin red, Pro con paquete y un nombre largo, a 640 no cabia junto lo
   que los cortes de ancho dejaban ver). La barra mide despues de pintar (antes de que se vea) y, si su contenido no
   cabe, sube un escalon y vuelve a medir. Los escalones, en este orden, quitan:
     1. el nombre;  2. el texto de "Offline mode" (queda la nube);  3. el texto largo del chip de creditos (queda el
     corto);  4. la liga "Buy credits" (sigue dentro del panel);  5. el chip del plan;  6. el icono del chip de
     creditos y algo de aire.
   Al cambiar el ancho de la ventana o lo que se ensena, vuelve a empezar desde el escalon 0. Los cortes de ancho de
   siempre (corto abajo de 640, el nombre desde 768, el chip del plan fuera en 380 o menos) siguen debajo. */
export const ESCALONES_DE_LA_BARRA = 6;

/** ¿Lo de la derecha no cabe? (lo usado contra lo disponible, con medio pixel de tolerancia). */
export function noCabe(barra, logo, derecha) {
  if (!barra || !logo || !derecha) return false;
  const estilo = window.getComputedStyle(barra);
  const relleno = (parseFloat(estilo.paddingLeft) || 0) + (parseFloat(estilo.paddingRight) || 0);
  const hueco = parseFloat(estilo.columnGap) || 0;
  const disponible = barra.clientWidth - relleno;
  const usado = logo.offsetWidth + hueco + derecha.scrollWidth;
  return usado > disponible + 0.5;
}

/** El texto del chip del plan, con las insignias de hoy. */
export function llaveDelPlan(planInfo, user) {
  const invitado = planInfo ? planInfo.is_guest : user?.is_guest;
  if (invitado) return 'dashboard:plan_badges.guest';
  const plan = planInfo ? planInfo.plan : user?.plan;
  return plan === 'pro' ? 'dashboard:plan_badges.pro' : 'pricing:plans.free.name';
}

/** ¿Ve "Buy credits"? La condicion del Panel de hoy: con cuenta y con documentos. */
export function puedeComprarCreditos(user, planInfo) {
  if (!user || user.is_guest) return false;
  return Number(planInfo?.documents_used || 0) > 0;
}

export default function BarraSuperior({ esqueleto = false }) {
  const { user } = useAuth();
  const { t } = useTranslation(['common', 'dashboard', 'pricing']);
  const { isOffline } = useNetworkStatus();
  const ruta = useRutaDeLaBarra();
  const { planInfo, usage, sinRed, listo } = useCreditosDeLaSesion();
  const [compraAbierta, setCompraAbierta] = useState(false);
  const enElPanel = ruta === RUTA_DEL_PANEL;

  // Otro usuario (o nadie): lo de la sesion anterior no se ensena. El dueno lo guarda el modulo de los creditos,
  // porque esta barra se desmonta al cerrar sesion.
  const idActual = user?.id ?? null;
  useEffect(() => {
    if (!esqueleto) asegurarDueno(idActual);
  }, [idActual, esqueleto]);

  // La primera carga: fuera del Panel, una vez. En el Panel solo se toma la copia que el Panel ya tiene en memoria.
  useEffect(() => {
    if (esqueleto || !user || listo) return;
    if (enElPanel) { tomarCopiaDelPanel(); return; }
    cargarCreditosSiFaltan(user);
  }, [esqueleto, user, listo, enElPanel]);

  // Los momentos de hoy, fuera del Panel (en el Panel los cubre el Panel): cambio de plan y vuelta a la pestana.
  const ultimaVuelta = useRef(0);
  useEffect(() => {
    if (esqueleto || !user || enElPanel) return undefined;
    const alCambiarElPlan = () => { refrescarCreditos(user); };
    const alVolver = () => {
      if (shouldRefetchOnRefocus(Date.now(), ultimaVuelta.current, document.visibilityState)) {
        ultimaVuelta.current = Date.now();
        refrescarCreditos(user);
      }
    };
    const dejarElPlan = subscribePlanChanged(alCambiarElPlan);
    window.addEventListener('focus', alVolver);
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      dejarElPlan();
      window.removeEventListener('focus', alVolver);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [esqueleto, user, enElPanel]);

  const conSesion = !esqueleto && !!user;
  const invitado = !!user?.is_guest;
  const puedeComprar = puedeComprarCreditos(user, planInfo);
  const apagado = sinRed || isOffline;

  // Vuelta 2 (D4): el escalon de la barra (ver ESCALONES_DE_LA_BARRA).
  const barraRef = useRef(null);
  const logoRef = useRef(null);
  const derechaRef = useRef(null);
  const [escalon, setEscalon] = useState(0);
  const [anchoVentana, setAnchoVentana] = useState(() => (typeof window === 'undefined' ? 0 : window.innerWidth));
  // Las letras de la casa llegan despues del primer pintado y cambian lo que mide el logo: al llegar, se vuelve a medir.
  const [letras, setLetras] = useState(0);
  useEffect(() => {
    let vivo = true;
    const alCambiar = () => setAnchoVentana(window.innerWidth);
    window.addEventListener('resize', alCambiar);
    window.addEventListener('orientationchange', alCambiar);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { if (vivo) setLetras((n) => n + 1); }).catch(() => {});
    return () => {
      vivo = false;
      window.removeEventListener('resize', alCambiar);
      window.removeEventListener('orientationchange', alCambiar);
    };
  }, []);
  const huella = [anchoVentana, letras, conSesion, isOffline, sinRed, user?.name, user?.email, invitado, puedeComprar,
    usage?.subscription_remaining, usage?.limit, usage?.pack_credits, usage?.credit_reset_date, planInfo?.plan,
    t('common:chasis.creditos_titulo')].join('|');
  const huellaVista = useRef(huella);
  useLayoutEffect(() => {
    if (huellaVista.current !== huella) {
      huellaVista.current = huella;
      if (escalon !== 0) { setEscalon(0); return; }
    }
    if (escalon < ESCALONES_DE_LA_BARRA && noCabe(barraRef.current, logoRef.current, derechaRef.current)) {
      setEscalon(escalon + 1);
    }
  }, [huella, escalon]);
  const compacta = escalon >= 6;

  return (
    <>
    <header
      ref={barraRef}
      className={`fixed top-0 left-0 right-0 ${BARRA_ALTO_CLASS} pt-[env(safe-area-inset-top)] z-50 flex items-center justify-between ${compacta ? 'gap-1.5 px-3' : 'gap-2 px-4'} bg-[#0D0D0D] border-b border-[#1C1C1C]`}
      data-testid="app-header"
      data-escalon={escalon}
    >
      {/* Vuelta 3 (D22): la barra se queda en el nivel 50 de siempre y antes en el orden de la pagina, asi que toda
          ventana de nivel 50 de cualquier pantalla queda encima de ella. El panel de creditos no cuelga de aqui: va en
          su propia capa (ver ChipDeCreditos). */}
      {/* Liga del router y no un ancla: un ancla recarga el documento y reinicia la app en el WebView. */}
      {/* Vuelta 2 (D10): el logo mide 24 de alto; su area de toque crece a 44 con un span vacio, absoluto y centrado
          (la tecnica de la casa), sin cambiar el dibujo. */}
      {/* borrar-rotas (A.6 a): desde la tablet el nombre arranca en la misma linea que los iconos del menu de la
          izquierda (ALINEACION_DEL_NOMBRE); en el telefono, donde no hay menu a la vista, se queda donde estaba. */}
      <Link to="/" ref={logoRef} className={`relative shrink-0 font-['Space_Grotesk'] font-bold text-base tracking-tight hover:opacity-80 transition-opacity ${ALINEACION_DEL_NOMBRE}`} data-testid="header-logo-link">
        <span aria-hidden="true" className="absolute left-1/2 top-1/2 h-11 w-full min-w-[44px] -translate-x-1/2 -translate-y-1/2" data-area-de-toque="si" />
        Quiz<span className="text-[#FF6B00]">essa</span>
      </Link>
      {conSesion && (
        <div ref={derechaRef} className={`flex min-w-0 items-center ${compacta ? 'gap-1.5' : 'gap-2'}`} data-testid="barra-derecha">
          <OfflinePill conTexto={escalon < 2} />
          <ChipDeCreditos
            usage={usage}
            sinRed={apagado}
            puedeComprar={puedeComprar}
            onComprar={() => setCompraAbierta(true)}
            corto={escalon >= 3}
            compacto={compacta}
          />
          {puedeComprar && usage && escalon < 4 && (
            <button
              type="button"
              onClick={() => setCompraAbierta(true)}
              disabled={apagado}
              title={apagado ? t('common:offline.need_connection') : undefined}
              className="hidden sm:inline-flex shrink-0 items-center gap-1.5 h-11 text-[13px] font-semibold text-[#F7A06A] hover:text-[#ffb98a] active:text-[#ffb98a] active:duration-0 transition-colors disabled:opacity-45 disabled:cursor-not-allowed whitespace-nowrap"
              data-testid="barra-comprar-creditos"
            >
              <ShoppingCart className="w-3.5 h-3.5" aria-hidden="true" />
              {t('dashboard:credits.buy_btn')}
            </button>
          )}
          {escalon < 5 && (
            <span
              className={`shrink-0 items-center h-6 rounded-full border border-[#3A2414] bg-[#241710] px-2 text-[12px] font-semibold text-[#F7A06A] whitespace-nowrap ${isOffline ? 'hidden sm:inline-flex max-[380px]:hidden' : 'inline-flex max-[380px]:hidden'}`}
              data-testid="chip-plan"
            >
              {t(llaveDelPlan(planInfo, user))}
            </span>
          )}
          <UserMenu conNombre={!invitado && escalon < 1} />
        </div>
      )}
    </header>
    {/* La ventana de compra va como hermana de la barra, nunca dentro: la barra es fija con su propio nivel, y la
        ventana quedaria debajo de la barra de abajo. Es la MISMA ventana de hoy; al cerrarla se refrescan los
        creditos (la compra de un paquete es uno de los momentos de hoy). */}
    {compraAbierta && (
      <Suspense fallback={null}>
        <CreditPackModal
          open
          onClose={() => { setCompraAbierta(false); refrescarCreditos(user); }}
        />
      </Suspense>
    )}
    </>
  );
}
